import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Database,
  Mail,
  FileText,
  BookOpen,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const ICON_BY_ID = {
  alegra: Database,
  cxc_flujo: FileText,
  dte: Mail,
  contabilizacion: BookOpen,
};

const HEALTH_STYLES = {
  ok: {
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
    label: 'OK',
  },
  warn: {
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    icon: AlertTriangle,
    label: 'Por actualizar',
  },
  error: {
    badge: 'bg-rose-100 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    icon: XCircle,
    label: 'Error / Stale',
  },
};

function formatTimestamp(ts) {
  if (!ts) return 'Nunca';
  try {
    return new Date(ts).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function formatHoursSince(h) {
  if (h == null) return '—';
  if (h < 1) return `Hace ${Math.round(h * 60)} min`;
  if (h < 24) return `Hace ${h.toFixed(1)} h`;
  return `Hace ${Math.floor(h / 24)} día${Math.floor(h / 24) === 1 ? '' : 's'}`;
}

export const SyncCenterWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState({}); // { sync_id: boolean }
  const [runningAll, setRunningAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/sync-center/status');
      setData(res.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerOne = async (sync) => {
    if (!sync.manual_trigger_supported) return;
    setRunning(prev => ({ ...prev, [sync.id]: true }));
    try {
      const res = await api.post(`/sync-center/run/${sync.id}`);
      const r = res.data || {};
      toast.success(`${sync.label}: actualizada`, {
        description: r.message ||
          (r.inserted != null ? `${r.inserted} nuevas · ${r.updated || 0} actualizadas · ${r.closed || 0} cerradas`
          : r.nuevos != null ? `${r.nuevos} nuevas reconciliadas`
          : 'Completado'),
      });
      await load();
    } catch (err) {
      toast.error(`Error en ${sync.label}`, {
        description: err?.response?.data?.detail || err.message || '',
      });
    } finally {
      setRunning(prev => ({ ...prev, [sync.id]: false }));
    }
  };

  const triggerAll = async () => {
    setRunningAll(true);
    try {
      const res = await api.post('/sync-center/run-all');
      const r = res.data?.results || {};
      const lines = Object.entries(r).map(([k, v]) =>
        v.ok ? `✓ ${k}` : `✗ ${k}: ${v.error || 'error'}`
      );
      toast.success('Sincronización global completada', {
        description: lines.join(' · '),
      });
      await load();
    } catch (err) {
      toast.error('Error sincronizando todo', {
        description: err?.response?.data?.detail || '',
      });
    } finally {
      setRunningAll(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-l-4 border-l-slate-400" data-testid="sync-center-widget">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando estado de sincronizaciones…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const overallStyle = HEALTH_STYLES[data.overall] || HEALTH_STYLES.ok;
  const OverallIcon = overallStyle.icon;
  const counts = data.counts || { ok: 0, warn: 0, error: 0 };

  return (
    <Card
      className={`border-l-4 ${
        data.overall === 'error' ? 'border-l-rose-500' :
        data.overall === 'warn' ? 'border-l-amber-500' :
        'border-l-emerald-500'
      }`}
      data-testid="sync-center-widget"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <span>Sync Center</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${overallStyle.badge} flex items-center gap-1`}>
              <OverallIcon className="w-3 h-3" />
              {counts.ok} OK · {counts.warn} aviso · {counts.error} error
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={triggerAll}
              disabled={runningAll}
              data-testid="sync-center-run-all"
              className="h-8"
            >
              {runningAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Actualizar TODO
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(v => !v)}
              className="h-8 px-2"
              data-testid="sync-center-toggle"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.syncs.map(s => {
              const Icon = ICON_BY_ID[s.id] || Database;
              const style = HEALTH_STYLES[s.health] || HEALTH_STYLES.warn;
              const isRunning = !!running[s.id];
              return (
                <div
                  key={s.id}
                  className="flex items-start gap-3 p-3 rounded-md border bg-white hover:bg-gray-50 transition"
                  data-testid={`sync-row-${s.id}`}
                >
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${style.dot}`} />
                  <Icon className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-gray-800 truncate">{s.label}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${style.badge}`}>
                        {style.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.schedule}</p>
                    <div className="flex items-baseline gap-2 mt-1 text-[11px]">
                      <span className="text-gray-700 font-medium">{formatHoursSince(s.hours_since)}</span>
                      <span className="text-muted-foreground">· {formatTimestamp(s.last_run)}</span>
                    </div>
                    {s.details && Object.keys(s.details).length > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-1 line-clamp-1">
                        {s.id === 'cxc_flujo' && s.details.updated != null &&
                          `${s.details.updated} CxC actualizadas · ${s.details.inserted || 0} nuevas`}
                        {s.id === 'dte' && s.details.imported != null &&
                          `${s.details.imported} importados · ${s.details.updated || 0} actualizados${s.details.errors ? ` · ${s.details.errors} errores` : ''}`}
                        {s.id === 'contabilizacion' && (s.details.nuevos != null || s.details.error) &&
                          (s.details.error ? `Error: ${s.details.error}` : `${s.details.nuevos} nuevas · ${s.details.total} totales`)}
                      </div>
                    )}
                  </div>
                  {s.manual_trigger_supported && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => triggerOne(s)}
                      disabled={isRunning || runningAll}
                      data-testid={`sync-run-${s.id}`}
                      className="h-7 px-2 flex-shrink-0"
                    >
                      {isRunning
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RefreshCw className="w-3 h-3" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
